/**
 * Centralized Option Premium Profile Engine — Options Oracle V4
 * 
 * 70% Institutional Value Area (VAL/VAH) & Gaussian Point of Control (POC) Engine:
 * 1. Historical Data: Last 2 trading days of Intraday History from Hugging Face .parquet datasets (cached in sessionStorage)
 * 2. Intraday History: Today's live 1-minute snapshots from Google Sheet / data bus / sliced playback timeline
 * 3. Live Option Feed: Upstox API continuous streaming data
 * 
 * Dynamically filtered by ATM ± Strike Range (ignoring ATM=0) and user selected subtab metrics:
 * (Combined OI, Combined Volume, Top OI, Top Volume, Top Chng OI, Vol-OI, Vol-OI-Chng)
 */
import { useState, useEffect, useRef, useMemo } from 'react'
import { getLastTradingDay, formatDateToYYYYMMDD } from '../lib/date-utils'
import { fetchHistoricalData } from '../lib/history-utils'
import { extractSnapshotOptions } from '../lib/snapshot-search'
import { useActiveHistory } from './useDataBus'
import { useSettingsStore } from '../stores/settingsStore'
import { usePlaybackStore } from '../stores/playbackStore'

export type TimeframeKey = '5m' | '10m' | '15m' | '20m' | '30m' | '40m' | '45m' | '60m'
export type BreakoutState = 'BREAKOUT' | 'BREAKDOWN' | 'ABOVE_POC' | 'BELOW_POC' | 'NEUTRAL'

export interface TimeframeLevels {
    poc: number
    support: number
    res: number
    strength: 'STRONG' | 'MODERATE' | 'NORMAL'
    strengthScore?: number
    state: BreakoutState
}

export interface StrikePremiumProfile {
    strike: number
    cePoc: number
    ceSupport: number
    ceRes: number
    ceStrength: 'STRONG' | 'MODERATE' | 'NORMAL'
    ceStrengthScore?: number
    cePocConfluence?: boolean
    ceSupportConfluence?: boolean
    ceResConfluence?: boolean

    pePoc: number
    peSupport: number
    peRes: number
    peStrength: 'STRONG' | 'MODERATE' | 'NORMAL'
    peStrengthScore?: number
    pePocConfluence?: boolean
    peSupportConfluence?: boolean
    peResConfluence?: boolean

    // Rolling Multi-Timeframe Intraday Breakout Engine (1m, 5m, 10m, 15m, 30m, 60m)
    ceTimeframes?: Record<TimeframeKey, TimeframeLevels>
    peTimeframes?: Record<TimeframeKey, TimeframeLevels>
    ceActiveSignal?: BreakoutState
    peActiveSignal?: BreakoutState
}

export interface StrikePremiumProfileResult {
    profileMap: Map<number, StrikePremiumProfile>
    isLoading: boolean
    daysLoaded: number
    error: string | null
}

const CACHE_PREFIX = 'hvn_prem_profile_v1_'

/**
 * Smart Price Binning: Groups option premium prices into institutional liquidity tiers
 */
export function binPrice(price: number): number {
    if (!price || isNaN(price) || price <= 0) return 0
    if (price < 20) return Math.round(price * 2) / 2         // ₹0.50 steps for low premiums
    if (price < 100) return Math.round(price)                // ₹1.00 steps for mid premiums
    if (price < 300) return Math.round(price / 2) * 2        // ₹2.00 steps for active ITM/ATM
    if (price < 500) return Math.round(price / 5) * 5        // ₹5.00 steps for deep ITM
    return Math.round(price / 10) * 10                       // ₹10.00 steps for ultra-deep ITM
}

/**
 * Rock-Solid Level Quantization: Rounds structural Support, Resistance & POC to definitive institutional trading increments
 */
export function quantizeLevel(price: number): number {
    if (!price || isNaN(price) || price <= 0) return 0
    if (price < 20) return Math.round(price * 2) / 2         // ₹0.50 increments
    if (price < 100) return Math.round(price)                // ₹1.00 increments
    if (price < 300) return Math.round(price / 2) * 2        // ₹2.00 increments
    if (price < 500) return Math.round(price / 5) * 5        // ₹5.00 increments
    return Math.round(price / 10) * 10                       // ₹10.00 increments
}

/**
 * Get past N trading days (excluding today or backtested date)
 */
function getPastTradingDays(count: number, baseDateStr?: string | null): string[] {
    const dates: string[] = []
    const baseDate = baseDateStr ? new Date(baseDateStr) : new Date()
    if (isNaN(baseDate.getTime())) {
        baseDate.setTime(Date.now())
    }
    const cursor = new Date(baseDate)
    cursor.setDate(cursor.getDate() - 1) // Start from yesterday

    for (let i = 0; i < count && dates.length < count; i++) {
        const tradingDay = getLastTradingDay(cursor)
        const dateStr = formatDateToYYYYMMDD(tradingDay)
        if (dateStr && !dates.includes(dateStr)) {
            dates.push(dateStr)
        }
        cursor.setTime(tradingDay.getTime())
        cursor.setDate(cursor.getDate() - 1)
    }

    return dates
}

/**
 * Extract weight for a specific option contract based on user's active subtab metric
 */
function getOptionMetricWeight(opt: any, isCall: boolean, activeSubTab: string): number {
    const callVol = Number(opt.callVolume ?? opt.ce_vol ?? (opt.type === 'CE' ? opt.volume : 0)) || 0
    const putVol = Number(opt.putVolume ?? opt.pe_vol ?? (opt.type === 'PE' ? opt.volume : 0)) || 0
    const callOi = Number(opt.callOpenInterest ?? opt.ce_oi ?? (opt.type === 'CE' ? opt.oi : 0)) || 0
    const putOi = Number(opt.putOpenInterest ?? opt.pe_oi ?? (opt.type === 'PE' ? opt.oi : 0)) || 0
    const callOiChg = Number(opt.callOpenInterestChange ?? opt.ce_oi_change ?? (opt.type === 'CE' ? opt.oiChange : 0)) || 0
    const putOiChg = Number(opt.putOpenInterestChange ?? opt.pe_oi_change ?? (opt.type === 'PE' ? opt.oiChange : 0)) || 0

    switch (activeSubTab) {
        case 'Combined OI':
            return (callOi + putOi) || (isCall ? callOi : putOi) || 1
        case 'Combined Volume':
            return (callVol + putVol) || (isCall ? callVol : putVol) || 1
        case 'Top OI':
            return (isCall ? callOi : putOi) || 1
        case 'Top Volume':
            return (isCall ? callVol : putVol) || 1
        case 'Top Chng OI':
            return Math.abs(isCall ? callOiChg : putOiChg) || 1
        case 'Vol-OI':
            return Math.abs((isCall ? callVol - callOi : putVol - putOi)) || 1
        case 'Vol-OI-Chng':
            return Math.abs((isCall ? callVol - callOiChg : putVol - putOiChg)) || 1
        default:
            return (isCall ? (callVol || callOi) : (putVol || putOi)) || 1
    }
}

/**
 * Parses snapshot array into Price -> Weight distribution map for CE and PE across strikes
 */
function extractPremiumDistributions(
    snapshots: any[],
    symbol: string,
    expiry: string,
    activeSubTab: string,
    targetMap: Map<string, Map<number, number>>,
    decayFactor: number = 1.0,
    dayAge: number = 0
) {
    if (!snapshots || snapshots.length === 0) return

    for (let i = 0; i < snapshots.length; i++) {
        const snap = snapshots[i]
        let opts: any[] = []

        if (snap.rawOpts && Array.isArray(snap.rawOpts)) {
            opts = snap.rawOpts
        } else {
            try {
                opts = extractSnapshotOptions(snap, symbol, expiry) || []
            } catch {
                continue
            }
        }

        if (opts.length === 0) continue

        const intradayProgress = snapshots.length > 1 ? i / (snapshots.length - 1) : 1.0
        const sessionWeight = (0.40 + 0.60 * intradayProgress) * decayFactor

        for (const opt of opts) {
            const strike = Number(opt.strike ?? opt.strikePrice ?? 0)
            if (!strike || isNaN(strike)) continue

            // CE Extraction
            if (opt.callPrice !== undefined || opt.ce_ltp !== undefined || opt.callLtp !== undefined || opt.type === 'CE') {
                let rawP = Number(opt.callPrice ?? opt.ce_ltp ?? opt.callLtp ?? opt.ltp ?? opt.price ?? 0)
                if (dayAge > 0 && rawP > 0) {
                    const thetaShift = Math.max(0.70, 1.0 - (dayAge * 0.08))
                    rawP = rawP * thetaShift
                }
                const binnedPrice = binPrice(rawP)
                if (binnedPrice > 0) {
                    const w = getOptionMetricWeight(opt, true, activeSubTab) * sessionWeight
                    if (w > 0) {
                        const strikeKey = `${strike}_CE`
                        let binMap = targetMap.get(strikeKey)
                        if (!binMap) {
                            binMap = new Map<number, number>()
                            targetMap.set(strikeKey, binMap)
                        }
                        binMap.set(binnedPrice, (binMap.get(binnedPrice) || 0) + w)
                    }
                }
            }

            // PE Extraction
            if (opt.putPrice !== undefined || opt.pe_ltp !== undefined || opt.putLtp !== undefined || opt.type === 'PE') {
                let rawP = Number(opt.putPrice ?? opt.pe_ltp ?? opt.putLtp ?? opt.ltp ?? opt.price ?? 0)
                if (dayAge > 0 && rawP > 0) {
                    const thetaShift = Math.max(0.70, 1.0 - (dayAge * 0.08))
                    rawP = rawP * thetaShift
                }
                const binnedPrice = binPrice(rawP)
                if (binnedPrice > 0) {
                    const w = getOptionMetricWeight(opt, false, activeSubTab) * sessionWeight
                    if (w > 0) {
                        const strikeKey = `${strike}_PE`
                        let binMap = targetMap.get(strikeKey)
                        if (!binMap) {
                            binMap = new Map<number, number>()
                            targetMap.set(strikeKey, binMap)
                        }
                        binMap.set(binnedPrice, (binMap.get(binnedPrice) || 0) + w)
                    }
                }
            }
        }
    }
}

/**
 * Institutional minimum price separation based on option premium tier.
 * Ensures Support Floor, POC, and Resistance Ceiling never crowd or show identical/similar values.
 */
function getMinSeparation(price: number): number {
    if (price <= 0) return 0
    if (price < 20) return Math.max(0.50, price * 0.08)
    if (price < 100) return Math.max(3.00, price * 0.06)
    if (price < 500) return Math.max(7.00, price * 0.06)
    return Math.max(15.00, price * 0.05)
}

interface LevelMeta {
    cacheKey?: string
    iv?: number
    straddleFloor?: number
    vwap?: number
    type?: 'CE' | 'PE'
    isExpiryDay?: boolean
    globalMaxWeight?: number
    tfResonanceMultiplier?: number
    isPlayback?: boolean
}

// Institutional Hysteresis Anti-Flicker Cache: Stores established levels to prevent DOM table flickering during live 2-sec Upstox streaming
const globalLevelHysteresisCache = new Map<string, { poc: number; support: number; res: number; strength: 'STRONG' | 'MODERATE' | 'NORMAL'; timestamp: number }>()

// Fast WeakMap cache for bin calculations to ensure 60fps buttery playback
const _binsResultCache = new WeakMap<Map<number, number>, Map<string, { poc: number; support: number; res: number; strength: 'STRONG' | 'MODERATE' | 'NORMAL'; strengthScore: number }>>()

/**
 * Calculates Best POC, Support, Resistance, and Strength from a price-weight distribution
 * Using 70% Institutional Value Area (VAL & VAH) Accumulation/Distribution Engine.
 */
function computeLevelsFromBins(
    bins: Map<number, number> | undefined,
    currentLtp: number,
    meta?: LevelMeta
): { poc: number; support: number; res: number; strength: 'STRONG' | 'MODERATE' | 'NORMAL'; strengthScore: number } {
    const referencePrice = currentLtp > 0 ? currentLtp : 0

    if (!bins || bins.size === 0) {
        return { 
            poc: quantizeLevel(referencePrice), 
            support: quantizeLevel(referencePrice > 0 ? referencePrice * 0.90 : 0), 
            res: quantizeLevel(referencePrice > 0 ? referencePrice * 1.10 : 0), 
            strength: 'NORMAL',
            strengthScore: 1
        }
    }

    const subKey = `${Math.round(referencePrice * 10)}_${meta?.cacheKey || ''}_${meta?.isExpiryDay || false}`
    let binCache = _binsResultCache.get(bins)
    if (binCache) {
        const cached = binCache.get(subKey)
        if (cached) return cached
    }

    const sortedPrices = Array.from(bins.keys()).sort((a, b) => a - b)
    const smoothedBins = new Map<number, number>()
    let totalWeight = 0
    let totalSmoothedWeight = 0

    for (const [, weight] of bins.entries()) {
        totalWeight += weight
    }

    let maxSmoothedWeight = -1
    let rawPoc: number = sortedPrices[0] ?? referencePrice ?? 0
    let rawMaxWeight = -1

    // Expiry Day Dynamic Gaussian Consolidation: Widen smoothing to 11 bins on Expiry Day to absorb near-zero premium fragmentation
    const isExpiry = meta?.isExpiryDay === true
    const resonance = meta?.tfResonanceMultiplier || 1.0

    for (let i = 0; i < sortedPrices.length; i++) {
        const p = sortedPrices[i]
        if (p === undefined) continue
        
        const w = (bins.get(p) || 0) * resonance
        const prevW = i > 0 ? ((bins.get(sortedPrices[i - 1]!) || 0) * resonance) : 0
        const nextW = i < sortedPrices.length - 1 ? ((bins.get(sortedPrices[i + 1]!) || 0) * resonance) : 0
        const prev2W = i > 2 ? ((bins.get(sortedPrices[i - 2]!) || 0) * resonance) : 0
        const next2W = i < sortedPrices.length - 2 ? ((bins.get(sortedPrices[i + 2]!) || 0) * resonance) : 0
        const prev3W = i > 3 ? ((bins.get(sortedPrices[i - 3]!) || 0) * resonance) : 0
        const next3W = i < sortedPrices.length - 3 ? ((bins.get(sortedPrices[i + 3]!) || 0) * resonance) : 0
        const prev4W = i > 4 ? ((bins.get(sortedPrices[i - 4]!) || 0) * resonance) : 0
        const next4W = i < sortedPrices.length - 4 ? ((bins.get(sortedPrices[i + 4]!) || 0) * resonance) : 0
        const prev5W = i > 5 ? ((bins.get(sortedPrices[i - 5]!) || 0) * resonance) : 0
        const next5W = i < sortedPrices.length - 5 ? ((bins.get(sortedPrices[i + 5]!) || 0) * resonance) : 0
        
        // 5-bin Gaussian-like kernel standard, 11-bin for Expiry Day Mega-Zone
        let smoothedW = w + 0.50 * prevW + 0.50 * nextW + 0.20 * prev2W + 0.20 * next2W
        if (isExpiry) {
            smoothedW += 0.10 * prev3W + 0.10 * next3W + 0.05 * prev4W + 0.05 * next4W + 0.02 * prev5W + 0.02 * next5W
        }
        
        smoothedBins.set(p, smoothedW)
        totalSmoothedWeight += smoothedW
        
        if (smoothedW > maxSmoothedWeight) {
            maxSmoothedWeight = smoothedW
            rawPoc = p
        }
        if (w > rawMaxWeight) {
            rawMaxWeight = w
        }
    }

    let bestPoc = binPrice(rawPoc) || referencePrice

    // Step 2: 70% Institutional Value Area (VA) Calculation
    const pocIdx = sortedPrices.indexOf(rawPoc) >= 0 ? sortedPrices.indexOf(rawPoc) : Math.floor(sortedPrices.length / 2)
    let valIdx = pocIdx
    let vahIdx = pocIdx
    let currentVAWeight = smoothedBins.get(sortedPrices[pocIdx] ?? bestPoc) || 0
    const targetVAWeight = totalSmoothedWeight * 0.70

    while (currentVAWeight < targetVAWeight && (valIdx > 0 || vahIdx < sortedPrices.length - 1)) {
        const nextDownIdx = valIdx > 0 ? valIdx - 1 : -1
        const nextUpIdx = vahIdx < sortedPrices.length - 1 ? vahIdx + 1 : -1

        const downWeight = nextDownIdx >= 0 ? (smoothedBins.get(sortedPrices[nextDownIdx]!) || 0) : -1
        const upWeight = nextUpIdx >= 0 ? (smoothedBins.get(sortedPrices[nextUpIdx]!) || 0) : -1

        if (downWeight >= upWeight && nextDownIdx >= 0) {
            valIdx = nextDownIdx
            currentVAWeight += downWeight
        } else if (nextUpIdx >= 0) {
            vahIdx = nextUpIdx
            currentVAWeight += upWeight
        } else if (nextDownIdx >= 0) {
            valIdx = nextDownIdx
            currentVAWeight += downWeight
        } else {
            break
        }
    }

    let bestSupport = quantizeLevel(sortedPrices[valIdx] ?? referencePrice)
    let bestRes = quantizeLevel(sortedPrices[vahIdx] ?? referencePrice)
    bestPoc = quantizeLevel(bestPoc)

    // Step 3: Institutional Structural Quantization & Enforce Minimum Separation
    // Ensure Support and Resistance don't collapse onto the POC, providing a definitive trading range.
    if (bestSupport > bestPoc) bestSupport = bestPoc
    if (bestRes < bestPoc) bestRes = bestPoc
    
    const minSep = getMinSeparation(bestPoc)
    if ((bestPoc - bestSupport) < minSep) bestSupport = quantizeLevel(bestPoc - minSep)
    if ((bestRes - bestPoc) < minSep) bestRes = quantizeLevel(bestPoc + minSep)

    const pocShare = totalWeight > 0 ? (rawMaxWeight / totalWeight) : 0
    const strength: 'STRONG' | 'MODERATE' | 'NORMAL' =
        pocShare >= 0.25 ? 'STRONG' : pocShare >= 0.14 ? 'MODERATE' : 'NORMAL'

    // Institutional Strength Score 1-10
    const pocMass = smoothedBins.get(rawPoc) || 1
    const globalMax = Math.max(1, meta?.globalMaxWeight || 1)
    const strengthScore = Math.min(10, Math.max(1, Math.ceil((pocMass / globalMax) * 10)))

    // Step 4: 24/7 Dethrone Rule (Anti-Flicker Protection in Live Mode)
    // Prevents flickering and wicks from jumping levels. A new bin must exceed the locked bin by 20%.
    if (meta?.cacheKey) {
        const cached = globalLevelHysteresisCache.get(meta.cacheKey)
        if (cached && referencePrice > 0 && !meta.isPlayback) {
            const newPocVol = smoothedBins.get(bestPoc) || 0
            const lockedPocVol = smoothedBins.get(cached.poc) || 0
            
            // If new POC doesn't beat locked POC by at least 20%, KEEP locked POC
            if (newPocVol < lockedPocVol * 1.20) {
                bestPoc = cached.poc
            }

            const newSupVol = smoothedBins.get(bestSupport) || 0
            const lockedSupVol = smoothedBins.get(cached.support) || 0
            if (newSupVol < lockedSupVol * 1.20) {
                bestSupport = cached.support
            }

            const newResVol = smoothedBins.get(bestRes) || 0
            const lockedResVol = smoothedBins.get(cached.res) || 0
            if (newResVol < lockedResVol * 1.20) {
                bestRes = cached.res
            }
        }

        globalLevelHysteresisCache.set(meta.cacheKey, { poc: bestPoc, support: bestSupport, res: bestRes, strength, timestamp: Date.now() })
    }

    const result = { poc: bestPoc, support: bestSupport, res: bestRes, strength, strengthScore }
    if (!binCache) {
        binCache = new Map()
        _binsResultCache.set(bins, binCache)
    }
    binCache.set(subKey, result)

    return result
}

/**
 * Diagnostic helper: Determines option contract breakout/breakdown momentum state
 */
function getBreakoutState(ltp: number, support: number, poc: number, res: number): BreakoutState {
    if (ltp <= 0) return 'NEUTRAL'
    if (res > 0 && ltp > res) return 'BREAKOUT'
    if (support > 0 && ltp < support) return 'BREAKDOWN'
    if (poc > 0 && ltp >= poc) return 'ABOVE_POC'
    if (poc > 0 && ltp < poc) return 'BELOW_POC'
    return 'NEUTRAL'
}

/**
 * Evaluates multi-timeframe quantitative consensus across 1m, 5m, 10m, 15m, 30m, and 60m windows
 */
function evaluateConfluenceSignal(tfLevels: Record<TimeframeKey, TimeframeLevels>): BreakoutState {
    const states = Object.values(tfLevels).map(t => t.state)
    const breakoutCount = states.filter(s => s === 'BREAKOUT').length
    const breakdownCount = states.filter(s => s === 'BREAKDOWN').length
    const abovePocCount = states.filter(s => s === 'ABOVE_POC' || s === 'BREAKOUT').length
    const belowPocCount = states.filter(s => s === 'BELOW_POC' || s === 'BREAKDOWN').length

    // Confluence Rule: Require at least two key timeframes to confirm dynamic breakouts or breakdowns
    if (breakoutCount >= 2 || (breakoutCount === 1 && abovePocCount >= 4)) return 'BREAKOUT'
    if (breakdownCount >= 2 || (breakdownCount === 1 && belowPocCount >= 4)) return 'BREAKDOWN'
    if (abovePocCount >= 4) return 'ABOVE_POC'
    if (belowPocCount >= 4) return 'BELOW_POC'
    return 'NEUTRAL'
}

export function useStrikePremiumProfile(
    symbol: string,
    strikeGap: number,
    indexLtp?: number,
    strikeRange?: number,
    activeSubTab: string = '',
    expiry: string = '',
    currentOptions: any[] = [],
    enabled: boolean = true,
    historyTimeline?: any[]
): StrikePremiumProfileResult {
    const [isLoading, setIsLoading] = useState(false)
    const [daysLoaded, setDaysLoaded] = useState(0)
    const [error, setError] = useState<string | null>(null)
    const [rawParquetSnaps, setRawParquetSnaps] = useState<any[][]>([])
    const fetchedRef = useRef<string>('')

    const historicalDate = useSettingsStore(s => s.historicalDate)
    const isPlaybackMode = !!historicalDate || (historyTimeline && historyTimeline.length > 0)
    const pastDates = useMemo(() => getPastTradingDays(2, historicalDate), [historicalDate])

    // 1. Fetch Today's Intraday History option chain snapshots from provided timeline OR fallback to reactive data bus
    const dataBusHistory = useActiveHistory(symbol) || []
    const rawTimeline = (historyTimeline && historyTimeline.length > 0) ? historyTimeline : dataBusHistory

    // Chronological timeline (strictly oldest to newest):
    // If incoming history is descending (newest first, like tableData / processedHistory), reverse it
    const chronologicalTimeline = useMemo(() => {
        if (!rawTimeline || rawTimeline.length === 0) return []
        const first = rawTimeline[0]
        const last = rawTimeline[rawTimeline.length - 1]
        const firstTime = new Date(first?.timestamp || first?.time || 0).getTime()
        const lastTime = new Date(last?.timestamp || last?.time || 0).getTime()
        const isDescending = rawTimeline.length > 1 && (firstTime > lastTime || (first?.time && last?.time && first.time > last.time))
        return isDescending ? [...rawTimeline].reverse() : rawTimeline
    }, [rawTimeline])

    // 2. Fetch Last 2 Trading Days from Hugging Face Parquet (cached in sessionStorage)
    useEffect(() => {
        if (!enabled || !symbol || strikeGap <= 0 || pastDates.length === 0) return

        const cacheKey = `${CACHE_PREFIX}${symbol}_${pastDates.join('_')}_${historicalDate || 'live'}`
        if (fetchedRef.current === cacheKey) return
        fetchedRef.current = cacheKey

        try {
            const cached = sessionStorage.getItem(cacheKey)
            if (cached) {
                const parsed = JSON.parse(cached)
                if (parsed && Array.isArray(parsed.snapshots) && parsed.daysLoaded > 0) {
                    setRawParquetSnaps(parsed.snapshots)
                    setDaysLoaded(parsed.daysLoaded)
                    setIsLoading(false)
                    return
                }
            }
        } catch {
            // Cache miss or quota issue, proceed to fetch
        }

        const controller = new AbortController()
        setIsLoading(true)
        setError(null)

        ;(async () => {
            try {
                const fetchedSnaps: any[][] = []
                let successCount = 0

                const results = await Promise.allSettled(
                    pastDates.map(async (date) => {
                        try {
                            // Pass empty string for expiry to prevent weekly format differences from blocking dataset extraction
                            const snapshots = await fetchHistoricalData(date, symbol, '')
                            if (snapshots && Array.isArray(snapshots) && snapshots.length > 0) {
                                return snapshots
                            }
                            return []
                        } catch (e) {
                            console.warn(`[PremiumProfile] Failed HF parquet fetch for ${date}:`, e)
                            return []
                        }
                    })
                )

                for (const res of results) {
                    if (res.status === 'fulfilled' && res.value && res.value.length > 0) {
                        fetchedSnaps.push(res.value)
                        successCount++
                    }
                }

                if (controller.signal.aborted) return

                setRawParquetSnaps(fetchedSnaps)
                setDaysLoaded(successCount)

                if (successCount > 0) {
                    try {
                        sessionStorage.setItem(cacheKey, JSON.stringify({
                            snapshots: fetchedSnaps,
                            daysLoaded: successCount,
                            timestamp: Date.now()
                        }))
                    } catch {
                        // Storage full or quota exceeded, silently ignore
                    }
                }
            } catch (e: any) {
                if (!controller.signal.aborted) {
                    setError(e?.message || 'Failed to fetch Hugging Face historical parquet data')
                    console.error('[PremiumProfile] Error:', e)
                }
            } finally {
                if (!controller.signal.aborted) {
                    setIsLoading(false)
                }
            }
        })()

        return () => { controller.abort() }
    }, [symbol, strikeGap, pastDates, expiry, enabled, historicalDate])

    // 3. Compute Per-Strike Support, Resistance & POC based on ATM ± range & Subtab Selection
    const profileMap = useMemo(() => {
        const resultMap = new Map<number, StrikePremiumProfile>()
        if (!symbol) return resultMap

        // Calculate ATM ± filtering boundaries strictly as per rules
        let minStrikeFilter = 0
        let maxStrikeFilter = Infinity

        if (indexLtp && indexLtp > 0 && strikeGap > 0 && strikeRange && strikeRange > 0) {
            const atmStrike = Math.round(indexLtp / strikeGap) * strikeGap
            minStrikeFilter = atmStrike - (strikeRange * strikeGap)
            maxStrikeFilter = atmStrike + (strikeRange * strikeGap)
        }

        // Aggregate price distributions across all time horizons (2 Days Parquet + Today Intraday + Live)
        const distMap = new Map<string, Map<number, number>>()

        // We separate the timelines to guarantee zero flickering:
        // 1. MACRO TIMELINE: 5-minute snapshots only. Completely excludes the hyper-volatile live 2-second tick.
        const macroIntradayTimeline = chronologicalTimeline.filter((_, index) => index % 5 === 0 || index === chronologicalTimeline.length - 1)

        // 2. ROLLING TIMELINE: 1-minute snapshots + live tick for momentum evaluation
        const rollingIntradayTimeline = currentOptions.length > 0
            ? [...chronologicalTimeline, { rawOpts: currentOptions, timestamp: Date.now() }]
            : [...chronologicalTimeline]

        if (macroIntradayTimeline.length > 3) {
            // We have a solid intraday session established (> 15 mins)
            // Decouple multi-day overnight volume to let today's true established profile shine!
            extractPremiumDistributions(macroIntradayTimeline, symbol, '', activeSubTab, distMap, 1.0)
        } else {
            // If the intraday timeline is too thin, we anchor with historical Parquet overnight data.
            const totalHistDays = rawParquetSnaps.length
            rawParquetSnaps.forEach((daySnaps, idx) => {
                const ageDays = totalHistDays - idx
                const recencyFactor = ageDays === 1 ? 0.70 : ageDays >= 2 ? 0.50 : 1.0
                extractPremiumDistributions(daySnaps, symbol, '', activeSubTab, distMap, recencyFactor, ageDays)
            })
            
            // Layer the thin stabilized intraday data on top of the historical anchor
            if (macroIntradayTimeline.length > 0) {
                extractPremiumDistributions(macroIntradayTimeline, symbol, '', activeSubTab, distMap, 1.0, 0)
            }
        }

        // Build Rolling Multi-Timeframe Intraday distributions 
        // Bins inside Bins Resonance Multipliers
        const tfConfigs: { key: TimeframeKey; count: number }[] = [
            { key: '5m', count: 5 },
            { key: '10m', count: 10 },
            { key: '15m', count: 15 },
            { key: '20m', count: 20 },
            { key: '30m', count: 30 },
            { key: '40m', count: 40 },
            { key: '45m', count: 45 },
            { key: '60m', count: 60 }
        ]
        const tfDistMaps: Record<TimeframeKey, Map<string, Map<number, number>>> = {
            '5m': new Map(), '10m': new Map(), '15m': new Map(), '20m': new Map(), '30m': new Map(), '40m': new Map(), '45m': new Map(), '60m': new Map()
        }

        const isPlaying = usePlaybackStore.getState().isPlaying
        if (!isPlaying) {
            for (const { key, count } of tfConfigs) {
                const slice = rollingIntradayTimeline.slice(-count)
                if (slice.length > 0) {
                    extractPremiumDistributions(slice, symbol, '', activeSubTab, tfDistMaps[key], 1.0, 0)
                }
            }
        }

        // Build live/historical LTP lookup & Option Writer Metadata map for Support / Resistance anchoring
        const ceLtpMap = new Map<number, number>()
        const peLtpMap = new Map<number, number>()
        const ceMetaMap = new Map<number, LevelMeta>()
        const peMetaMap = new Map<number, LevelMeta>()

        let referenceOpts = currentOptions
        if (referenceOpts.length === 0 && rollingIntradayTimeline.length > 0) {
            const latestSnap = rollingIntradayTimeline[rollingIntradayTimeline.length - 1]
            if (latestSnap?.rawOpts && Array.isArray(latestSnap.rawOpts)) {
                referenceOpts = latestSnap.rawOpts
            } else {
                try {
                    referenceOpts = extractSnapshotOptions(latestSnap, symbol, expiry) || []
                } catch {}
            }
        }

        for (const opt of referenceOpts) {
            const s = Number(opt.strike ?? opt.strikePrice ?? 0)
            if (!s || isNaN(s)) continue
            let ceP = 0, peP = 0
            if (opt.callPrice !== undefined || opt.ce_ltp !== undefined || opt.callLtp !== undefined) {
                ceP = Number(opt.callPrice ?? opt.ce_ltp ?? opt.callLtp ?? 0)
                ceLtpMap.set(s, ceP)
            } else if (opt.type === 'CE') {
                ceP = Number(opt.ltp ?? opt.price ?? 0)
                ceLtpMap.set(s, ceP)
            }
            if (opt.putPrice !== undefined || opt.pe_ltp !== undefined || opt.putLtp !== undefined) {
                peP = Number(opt.putPrice ?? opt.pe_ltp ?? opt.putLtp ?? 0)
                peLtpMap.set(s, peP)
            } else if (opt.type === 'PE') {
                peP = Number(opt.ltp ?? opt.price ?? 0)
                peLtpMap.set(s, peP)
            }

            const straddleTotal = (ceLtpMap.get(s) || 0) + (peLtpMap.get(s) || 0)
            const straddleFloor = straddleTotal > 0 ? Math.max(0.5, binPrice(straddleTotal * 0.18)) : undefined
            const ceIv = Number(opt.ce_implied_volatility ?? opt.ceIv ?? opt.iv ?? 0)
            const peIv = Number(opt.pe_implied_volatility ?? opt.peIv ?? opt.iv ?? 0)

            ceMetaMap.set(s, {
                cacheKey: `${symbol}_${s}_CE`,
                iv: ceIv,
                straddleFloor,
                type: 'CE'
            })
            peMetaMap.set(s, {
                cacheKey: `${symbol}_${s}_PE`,
                iv: peIv,
                straddleFloor,
                type: 'PE'
            })
        }

        // Collect all unique strikes encountered across distributions & live chain
        const allStrikes = new Set<number>()
        for (const k of distMap.keys()) {
            const s = Number(k.split('_')[0])
            if (s && !isNaN(s)) allStrikes.add(s)
        }
        for (const s of ceLtpMap.keys()) allStrikes.add(s)
        for (const s of peLtpMap.keys()) allStrikes.add(s)

        // Pre-compute Absolute Global Max Weight for the 1-10 Strength Ranking System
        let globalMaxWeight = 1
        for (const bins of distMap.values()) {
            for (const w of bins.values()) {
                if (w > globalMaxWeight) globalMaxWeight = w
            }
        }

        // Expiry Day Check for Dynamic Gaussian Consolidation
        const todayStr = formatDateToYYYYMMDD(new Date())
        const isExpiryDay = todayStr !== null && expiry.replace(/-/g, '') === todayStr.replace(/-/g, '')

        // In playback mode, create deterministic point-in-time cache keys to prevent leakage
        const latestTimeTag = isPlaybackMode && chronologicalTimeline.length > 0 
            ? (chronologicalTimeline[chronologicalTimeline.length - 1].time || chronologicalTimeline[chronologicalTimeline.length - 1].timestamp || 'point_in_time')
            : 'live'

        for (const strike of allStrikes) {
            if (strike < minStrikeFilter || strike > maxStrikeFilter) continue

            const ceBins = distMap.get(`${strike}_CE`)
            const peBins = distMap.get(`${strike}_PE`)

            const ceLtp = ceLtpMap.get(strike) || 0
            const peLtp = peLtpMap.get(strike) || 0

            const cacheKeyCE = `${symbol}_${historicalDate || 'live'}_${strike}_CE`
            const cacheKeyPE = `${symbol}_${historicalDate || 'live'}_${strike}_PE`

            const ceMeta = ceMetaMap.get(strike) || { cacheKey: cacheKeyCE, type: 'CE' as const }
            const peMeta = peMetaMap.get(strike) || { cacheKey: cacheKeyPE, type: 'PE' as const }

            // Timeframe Resonance (Bins inside Bins) - Combine tfDistMaps to find resonant peaks
            const computeResonance = (baseBins: Map<number, number> | undefined, strikeKey: string) => {
                if (isPlaying || !baseBins) return 1.0
                let maxTfHits = 1
                for (const p of baseBins.keys()) {
                    let hits = 0
                    for (const { key } of tfConfigs) {
                        const tfBin = tfDistMaps[key]?.get(strikeKey)
                        if (tfBin && tfBin.has(p) && (tfBin.get(p) || 0) > 0) hits++
                    }
                    if (hits > maxTfHits) maxTfHits = hits
                }
                return 1.0 + (maxTfHits * 0.15) // Exponential resonance multiplier
            }
            
            const ceResonance = computeResonance(ceBins, `${strike}_CE`)
            const peResonance = computeResonance(peBins, `${strike}_PE`)

            const ceLevels = computeLevelsFromBins(ceBins, ceLtp, {
                ...ceMeta,
                cacheKey: cacheKeyCE,
                isExpiryDay,
                globalMaxWeight,
                tfResonanceMultiplier: ceResonance,
                isPlayback: isPlaybackMode
            })
            const peLevels = computeLevelsFromBins(peBins, peLtp, {
                ...peMeta,
                cacheKey: cacheKeyPE,
                isExpiryDay,
                globalMaxWeight,
                tfResonanceMultiplier: peResonance,
                isPlayback: isPlaybackMode
            })

            // Calculate per-timeframe Support, POC, Resistance and momentum state
            const ceTimeframes = {} as Record<TimeframeKey, TimeframeLevels>
            const peTimeframes = {} as Record<TimeframeKey, TimeframeLevels>
            let ceActiveSignal: BreakoutState = 'NEUTRAL'
            let peActiveSignal: BreakoutState = 'NEUTRAL'
            let cePocConfluence = false
            let ceSupportConfluence = false
            let ceResConfluence = false
            let pePocConfluence = false
            let peSupportConfluence = false
            let peResConfluence = false

            if (!isPlaying) {
                for (const { key } of tfConfigs) {
                    // Fallback to structural baseline if intraday window has zero trades
                    const ceTfBins = tfDistMaps[key].get(`${strike}_CE`) || ceBins
                    const peTfBins = tfDistMaps[key].get(`${strike}_PE`) || peBins
                    const ceTfRes = computeLevelsFromBins(ceTfBins, ceLtp, { ...ceMeta, cacheKey: `${cacheKeyCE}_${key}`, isExpiryDay, globalMaxWeight, isPlayback: isPlaybackMode })
                    const peTfRes = computeLevelsFromBins(peTfBins, peLtp, { ...peMeta, cacheKey: `${cacheKeyPE}_${key}`, isExpiryDay, globalMaxWeight, isPlayback: isPlaybackMode })

                    ceTimeframes[key] = {
                        poc: ceTfRes.poc || ceLtp,
                        support: ceTfRes.support || ceLtp,
                        res: ceTfRes.res || ceLtp,
                        strength: ceTfRes.strength,
                        strengthScore: ceTfRes.strengthScore,
                        state: getBreakoutState(ceLtp, ceTfRes.support, ceTfRes.poc, ceTfRes.res)
                    }
                    peTimeframes[key] = {
                        poc: peTfRes.poc || peLtp,
                        support: peTfRes.support || peLtp,
                        res: peTfRes.res || peLtp,
                        strength: peTfRes.strength,
                        strengthScore: peTfRes.strengthScore,
                        state: getBreakoutState(peLtp, peTfRes.support, peTfRes.poc, peTfRes.res)
                    }
                }

                ceActiveSignal = evaluateConfluenceSignal(ceTimeframes)
                peActiveSignal = evaluateConfluenceSignal(peTimeframes)

                // Multi-Timeframe Resonance & Confluence Validation:
                // High confidence when intraday timeframes (15m, 30m, 60m) agree with structural macro level within ±1.5%
                const checkConfluence = (macroVal: number, tfMap: Record<TimeframeKey, TimeframeLevels>, field: 'poc' | 'support' | 'res') => {
                    if (!macroVal || macroVal <= 0) return false
                    const threshold = Math.max(getMinSeparation(macroVal), macroVal * 0.015)
                    const tf15 = tfMap['15m']?.[field]
                    const tf30 = tfMap['30m']?.[field]
                    const tf60 = tfMap['60m']?.[field]
                    let matches = 0
                    if (tf15 !== undefined && Math.abs(tf15 - macroVal) <= threshold) matches++
                    if (tf30 !== undefined && Math.abs(tf30 - macroVal) <= threshold) matches++
                    if (tf60 !== undefined && Math.abs(tf60 - macroVal) <= threshold) matches++
                    return matches >= 2
                }

                cePocConfluence = checkConfluence(ceLevels.poc || ceLtp, ceTimeframes, 'poc')
                ceSupportConfluence = checkConfluence(ceLevels.support || ceLtp, ceTimeframes, 'support')
                ceResConfluence = checkConfluence(ceLevels.res || ceLtp, ceTimeframes, 'res')

                pePocConfluence = checkConfluence(peLevels.poc || peLtp, peTimeframes, 'poc')
                peSupportConfluence = checkConfluence(peLevels.support || peLtp, peTimeframes, 'support')
                peResConfluence = checkConfluence(peLevels.res || peLtp, peTimeframes, 'res')
            }

            resultMap.set(strike, {
                strike,
                cePoc: ceLevels.poc || ceLtp,
                ceSupport: ceLevels.support || ceLtp,
                ceRes: ceLevels.res || ceLtp,
                ceStrength: ceLevels.strength,
                ceStrengthScore: ceLevels.strengthScore,
                cePocConfluence,
                ceSupportConfluence,
                ceResConfluence,
                pePoc: peLevels.poc || peLtp,
                peSupport: peLevels.support || peLtp,
                peRes: peLevels.res || peLtp,
                peStrength: peLevels.strength,
                peStrengthScore: peLevels.strengthScore,
                pePocConfluence,
                peSupportConfluence,
                peResConfluence,
                ceTimeframes: isPlaying ? undefined : ceTimeframes,
                peTimeframes: isPlaying ? undefined : peTimeframes,
                ceActiveSignal: isPlaying ? undefined : ceActiveSignal,
                peActiveSignal: isPlaying ? undefined : peActiveSignal
            })
        }

        return resultMap
    }, [symbol, rawParquetSnaps, chronologicalTimeline, currentOptions, indexLtp, strikeGap, strikeRange, activeSubTab, expiry, historicalDate, isPlaybackMode])

    return { profileMap, isLoading, daysLoaded, error }
}
